// ---------------------------------------------------------------------------
// KG channels message shape and defensive parsers.
//
// Mirrors the Python `serialize_message_history` output (snake_case fields
// as emitted by worker-py):
//
//   messages.json  ?  { [channelName]: KgMessage[] }
//   metadata.json  ?  KgMetadata
//
// We parse defensively � every field is optional and we tolerate extras so
// upstream additions don't break the UI. When parsing fails, the renderer
// falls back to "show raw JSON".
// ---------------------------------------------------------------------------

export interface KgMessage {
  msg_node?: string;
  author?: string;
  role?: string;
  content?: string;
  timestamp?: string;
  reply_to?: string | null;
  tone?: string;
  style?: string;
  expertise?: string;
  path_type?: string;
  phase_name?: string;
  phase_progress?: string;
  stage?: string;
  topic?: string;
  is_noise?: boolean;
  is_decision_point?: boolean;
  decision_type?: string;
  decision_change_metadata?: Record<string, unknown>;
}

export interface KgChannel {
  name: string;
  messages: KgMessage[];
}

export interface KgMetadata {
  domain?: string;
  scenario?: string;
  messagesGenerated?: number;
  users?: string[];
  projects?: string[];
  totalPhases?: number;
  numTopics?: number;
  numPhases?: number;
  generationTimeSeconds?: number;
  openaiModel?: string;
  noiseRatio?: number;
  expertiseBias?: string;
  tones?: string[];
  styles?: string[];
  pathWeights?: { post?: number; reply?: number; cross_project_reply?: number };
}

export interface KgParsedResult {
  channels: KgChannel[];
  metadata: KgMetadata | null;
}

/**
 * Parse the messages JSON. The Python pipeline writes an object keyed by
 * channel name; each value is the message array. Returns `null` if the body
 * isn't a parseable object of that shape.
 */
export function parseChannels(messagesJson: string): KgChannel[] | null {
  let raw: unknown;
  try {
    raw = JSON.parse(messagesJson);
  } catch {
    return null;
  }
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const channels: KgChannel[] = [];
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue;
    channels.push({
      name,
      messages: value.filter(
        (m): m is KgMessage => typeof m === 'object' && m !== null,
      ),
    });
  }
  return channels;
}

export function parseMetadata(
  metadataJson: string | undefined,
): KgMetadata | null {
  if (metadataJson === undefined) return null;
  try {
    const raw: unknown = JSON.parse(metadataJson);
    if (raw === null || typeof raw !== 'object') return null;
    return raw as KgMetadata;
  } catch {
    return null;
  }
}
